import React, { useState, useEffect, useRef } from 'react';
import { HiOutlineRefresh } from 'react-icons/hi';
import { getManagementPendingOrders, getOrderById, getManagementOrderById } from '../services/api';
import { refreshAccessToken } from '../services/auth';
import { formatDateTime, getStatusColor, handleApiError } from '../utils/helpers';
import ManagementOrderDetailsView from './ManagementOrderDetailsView';
import SuccessModal from '../components/common/SuccessModal';
import WholesalerName from '../components/common/WholesalerName';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';

const ManagementPendingOrders = () => {
    const [orders, setOrders] = useState([]);
    const [filteredOrders, setFilteredOrders] = useState([]);
    const [originalOrders, setOriginalOrders] = useState([]); // Store original page data
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [searchQuery, setSearchQuery] = useState('');
    const [dealerFilter, setDealerFilter] = useState('all');
    const [selectedOrder, setSelectedOrder] = useState(null);
    const [detailsLoading, setDetailsLoading] = useState(false);
    const [detailsError, setDetailsError] = useState(null);
    const [dealers, setDealers] = useState([]); // For dealer filter
    const [page, setPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);
    const [totalElements, setTotalElements] = useState(0);
    const [totalFromAPI, setTotalFromAPI] = useState(0); // Store total from API
    const [itemsPerPage] = useState(20);
    const [searchLoading, setSearchLoading] = useState(false);

    // Success modal state
    const [showSuccessModal, setShowSuccessModal] = useState(false);
    const [successModalData, setSuccessModalData] = useState({ orderId: '', action: '', status: '' });

    const searchTimeoutRef = useRef(null);
    const abortControllerRef = useRef(null);
    const autoRetryRef = useRef(false);

    const navigate = useNavigate();
    const { accessToken } = useAuth();

    // Calculate pagination for filtered results
    const calculatePagination = (filteredData) => {
        const total = filteredData.length;
        const pages = Math.ceil(total / itemsPerPage);
        setTotalPages(pages);
        setTotalElements(total);

        // Reset to page 1 if current page is beyond new total pages
        if (page > pages && pages > 0) {
            setPage(1);
        }
    };

    // Get current page data - for server-side pagination, this is just the current orders
    const getCurrentPageData = () => {
        // Since we're using server-side pagination, the current orders are already the page data
        return filteredOrders;
    };

    // Fetch all pending orders (paginated)
    const fetchOrders = async () => {
        try {
            setLoading(true);
            // Get management pending orders (paginated, API expects 0-based page)
            const data = await getManagementPendingOrders({ page: page - 1, size: 20 });
            const pageOrders = data.content || [];
            setOrders(pageOrders);
            setOriginalOrders(pageOrders); // Store original page data
            setFilteredOrders(pageOrders);
            setTotalPages(Number(data.totalPages) || 1);
            setTotalElements(Number(data.totalElements) || 0);
            setTotalFromAPI(Number(data.totalElements) || 0); // Update totalFromAPI

            const newDealers = pageOrders.map(o => o.assignedDealer || o.dealerName).filter(Boolean);
            setDealers(prevDealers => {
                const combined = [...prevDealers, ...newDealers];
                return Array.from(new Set(combined)); // Remove duplicates
            });
        } catch (err) {
            if (handleApiError(err, navigate)) return;
            if (
                !autoRetryRef.current &&
                err &&
                typeof err.message === "string" &&
                err.message.includes("Request failed after repeated attempts")
            ) {
                autoRetryRef.current = true;
                setTimeout(() => {
                    fetchOrders();
                }, 1000);
                return;
            }
            setError('Failed to fetch pending orders');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchOrders();
        // eslint-disable-next-line
    }, [accessToken, page]);

    // Show success modal after order update
    useEffect(() => {
        const urlParams = new URLSearchParams(window.location.search);
        if (urlParams.get('updated') === 'true') {
            const orderId = urlParams.get('orderId');
            setSuccessModalData({
                orderId: orderId || '',
                action: 'updated',
                status: 'Order quantities updated successfully'
            });
            setShowSuccessModal(true);
            // Clean up the URL parameter
            urlParams.delete('updated');
            urlParams.delete('orderId');
            window.history.replaceState({}, '', window.location.pathname + (urlParams.toString() ? '?' + urlParams.toString() : ''));
        }
    }, []);

    // Apply dealer filter when original orders change
    useEffect(() => {
        let filtered = originalOrders;
        if (dealerFilter !== 'all') {
            filtered = filtered.filter(order => (order.assignedDealer || order.dealerName) === dealerFilter);
        }
        setFilteredOrders(filtered);
        setTotalElements(filtered.length);
    }, [originalOrders, dealerFilter]);

    // Utility: check if query is likely an order ID (all digits or starts with 'ORD-')
    const isOrderId = (query) => /^\d+$/.test(query) || /^ORD-?\d+$/i.test(query);

    // Synchronous input handler with debouncing
    const handleSearchChange = (e) => {
        const value = e.target.value;
        setSearchQuery(value);

        // Clear previous timeout
        if (searchTimeoutRef.current) {
            clearTimeout(searchTimeoutRef.current);
        }

        // Cancel previous request
        if (abortControllerRef.current) {
            abortControllerRef.current.abort();
        }

        // Create new abort controller for this request
        abortControllerRef.current = new AbortController();

        // Debounce the search
        searchTimeoutRef.current = setTimeout(() => {
            performSearch(value);
        }, 300); // 300ms delay
    };

    // Perform the actual search
    const performSearch = async (query) => {
        // Check if search query is empty or just whitespace
        if (!query || !query.trim()) {
            // No search query - reset to original page data with dealer filter
            let filtered = originalOrders;
            if (dealerFilter !== 'all') {
                filtered = filtered.filter(order => (order.assignedDealer || order.dealerName) === dealerFilter);
            }
            setFilteredOrders(filtered);
            setTotalElements(filtered.length);
            setSearchLoading(false);
            return;
        }

        // Check if it's a numeric search (all digits or starts with 'ORD-')
        const isNumericSearch = /^\d+$/.test(query) || /^ORD-?\d+$/i.test(query);

        if (isNumericSearch) {
            // Use backend API for all numeric searches to find orders across all pages
            setSearchLoading(true);
            setError(null);
            try {
                const order = await getManagementOrderById(query, 'Pending');
                // Check if this request was cancelled
                if (abortControllerRef.current?.signal.aborted) {
                    return;
                }
                if (order && order.id && order.status === 'PENDING') {
                    // Apply dealer filter to the found order
                    if (dealerFilter === 'all' || (order.assignedDealer || order.dealerName) === dealerFilter) {
                        setFilteredOrders([order]);
                        setTotalElements(1);
                    } else {
                        setFilteredOrders([]);
                        setTotalElements(0);
                    }
                } else {
                    // Order not found or not pending
                    setFilteredOrders([]);
                    setTotalElements(0);
                }
            } catch (err) {
                // Check if this request was cancelled
                if (abortControllerRef.current?.signal.aborted) {
                    return;
                }
                setFilteredOrders([]);
                setTotalElements(0);
            }
            setSearchLoading(false);
        } else {
            // Client-side filtering for non-numeric queries
            let filtered = originalOrders; // Use originalOrders to ensure we only filter pending orders
            filtered = filtered.filter(order =>
                String(order.id).toLowerCase().includes(query.toLowerCase()) ||
                (order.type && order.type.toLowerCase().includes(query.toLowerCase()))
            );
            // Apply dealer filter
            if (dealerFilter !== 'all') {
                filtered = filtered.filter(order => (order.assignedDealer || order.dealerName) === dealerFilter);
            }
            setFilteredOrders(filtered);
            setTotalElements(filtered.length);
            setSearchLoading(false);
        }
    };

    // Cleanup on unmount
    useEffect(() => {
        return () => {
            if (searchTimeoutRef.current) {
                clearTimeout(searchTimeoutRef.current);
            }
            if (abortControllerRef.current) {
                abortControllerRef.current.abort();
            }
        };
    }, []);

    const handleDealerFilterChange = (e) => {
        const newFilter = e.target.value;
        setDealerFilter(newFilter);

        // Apply dealer filter to current data
        let filtered = originalOrders;
        if (newFilter !== 'all') {
            filtered = filtered.filter(order => (order.assignedDealer || order.dealerName) === newFilter);
        }
        setFilteredOrders(filtered);
        setTotalElements(filtered.length);
        setPage(1); // Reset to first page when filter changes
    };

    const handleViewDetails = (order) => {
        setSelectedOrder(order);
        setDetailsError(null);
    };

    const handleBackToList = () => {
        setSelectedOrder(null);
        setDetailsError(null);
    };

    const handleOrderUpdate = (orderId, newStatus, successData = null) => {
        // Remove the updated order from the filtered orders list
        setFilteredOrders(prev => prev.filter(order => order.id !== orderId));
        setTotalElements(prev => prev - 1);

        // Close the details view
        setSelectedOrder(null);
        setDetailsError(null);

        // Show success modal if success data is provided
        if (successData) {
            setSuccessModalData(successData);
            setShowSuccessModal(true);
        }


    };

    const handlePrevPage = () => setPage((p) => Math.max(1, p - 1));
    const handleNextPage = () => setPage((p) => Math.min(totalPages, p + 1));

    const handleCloseSuccessModal = () => {
        setShowSuccessModal(false);
        setSuccessModalData({ orderId: '', action: '', status: '' });
    };

    return (
        <div className="max-w-6xl mx-auto p-6">
            <div className="mb-8">
                <h1 className="text-2xl font-bold text-gray-900 mb-2">Management Pending Orders</h1>
                <p className="text-gray-600">Review and manage all pending orders. (Approve, reject, update)</p>
            </div>
            {selectedOrder ? (
                <div className="mb-6">
                    <button
                        className="mb-4 px-4 py-2 bg-gray-200 hover:bg-gray-300 rounded text-gray-800"
                        onClick={handleBackToList}
                    >
                        ← Back to List
                    </button>
                    {detailsLoading ? (
                        <div className="flex justify-center items-center h-32">
                            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
                        </div>
                    ) : detailsError ? (
                        (
                            <div className="bg-red-50 border border-red-200 text-red-600 px-4 py-3 rounded-md">{detailsError}</div>
                        )
                    ) : (
                        <ManagementOrderDetailsView order={selectedOrder} onOrderUpdate={handleOrderUpdate} />
                    )}
                </div>
            ) : (
                <>
                    {/* Filters */}
                    <div className="bg-white p-4 rounded-lg shadow-sm border border-gray-200 mb-6">
                        <div className="flex flex-col md:flex-row gap-4">
                            <div className="flex-1">
                                <label className="block text-sm font-medium text-gray-700 mb-1">Search Orders</label>
                                <input
                                    type="text"
                                    placeholder="Search by Order ID..."
                                    value={searchQuery}
                                    onChange={handleSearchChange}
                                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                                />
                                {searchLoading && (
                                    <div className="mt-2 text-sm text-gray-500">Searching...</div>
                                )}
                            </div>
                            <div className="md:w-48">
                                <label className="block text-sm font-medium text-gray-700 mb-1">Assigned Dealer</label>
                                <select
                                    value={dealerFilter}
                                    onChange={handleDealerFilterChange}
                                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                                >
                                    <option value="all">All Dealers</option>
                                    {dealers.map((dealer) => (
                                        <option key={dealer} value={dealer}>{dealer}</option>
                                    ))}
                                </select>
                            </div>
                        </div>
                    </div>
                    {/* Results count indicator - now right above the table */}
                    <div className="text-sm text-gray-600 mb-4">
                        Showing {getCurrentPageData().length} of {totalFromAPI} pending orders
                    </div>
                    {/* Orders Table or No Orders Message or Spinner/Error */}
                    <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
                        <div className="overflow-x-auto">
                            {loading ? (
                                <div className="flex justify-center items-center h-32">
                                    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
                                </div>
                            ) : error ? (
                                (
                                    <div className="bg-red-50 border border-red-200 text-red-600 px-4 py-3 rounded-md text-center">{error}</div>
                                )
                            ) : filteredOrders.length === 0 ? (
                                <div className="p-8 text-center">
                                    <p className="text-gray-500 text-lg">No pending orders found</p>
                                </div>
                            ) : (
                                <table className="min-w-full divide-y divide-gray-200">
                                    <thead className="bg-gray-50">
                                        <tr>
                                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Order ID</th>
                                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Dealer</th>
                                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Created</th>
                                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                                        </tr>
                                    </thead>
                                    <tbody className="bg-white divide-y divide-gray-200">
                                        {filteredOrders.map((order) => (
                                            <tr key={order.id} className="hover:bg-gray-50">
                                                <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">{order.id}</td>
                                                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                                    <div>{order.assignedDealer || order.dealerName || '-'}</div>
                                                    {order.wholesalerId && (
                                                        <WholesalerName wholesalerId={order.wholesalerId} />
                                                    )}
                                                </td>
                                                <td className="px-6 py-4 whitespace-nowrap">
                                                    <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getStatusColor(order.status)}`}>{order.status}</span>
                                                </td>
                                                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{formatDateTime(order.createdAt)}</td>
                                                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                                    <button className="text-blue-600 hover:text-blue-900 mr-3" onClick={() => handleViewDetails(order)}>View Details</button>
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            )}
                        </div>
                    </div>
                    {/* Pagination Bar (after the table, at the bottom) */}
                    {totalPages > 1 && (
                        <div className="flex items-center justify-between mt-6 mb-2 w-full">
                            {/* Prev button left-aligned */}
                            <button
                                onClick={handlePrevPage}
                                disabled={page === 1}
                                className={`flex items-center px-3 py-1 border rounded mr-2 text-base font-medium transition-colors duration-200 ${page === 1 ? 'bg-gray-100 text-gray-400 border-gray-200 cursor-not-allowed' : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'}`}
                                style={{ minWidth: 80 }}
                            >
                                <span className="mr-1">&#8592;</span> Prev
                            </button>
                            {/* Page numbers centered */}
                            <div className="flex space-x-2 justify-center flex-1">
                                {(() => {
                                    const pages = [];
                                    if (totalPages <= 5) {
                                        for (let p = 1; p <= totalPages; p++) {
                                            pages.push(p);
                                        }
                                    } else {
                                        if (page <= 3) {
                                            pages.push(1, 2, 3, 4, '...', totalPages);
                                        } else if (page >= totalPages - 2) {
                                            pages.push(1, '...', totalPages - 3, totalPages - 2, totalPages - 1, totalPages);
                                        } else {
                                            pages.push(1, '...', page - 1, page, page + 1, '...', totalPages);
                                        }
                                    }
                                    return pages.map((p, idx) =>
                                        p === '...'
                                            ? <span key={"ellipsis-" + idx} className="w-7 h-7 flex items-center justify-center text-base text-gray-400">...</span>
                                            : <button
                                                key={p}
                                                onClick={() => setPage(p)}
                                                className={`w-7 h-7 rounded text-base font-medium transition-colors duration-200 ${p === page ? 'bg-gray-200 text-gray-900' : 'bg-white text-gray-700 hover:bg-gray-100'}`}
                                            >
                                                {p}
                                            </button>
                                    );
                                })()}
                            </div>
                            {/* Next button right-aligned */}
                            <button
                                onClick={handleNextPage}
                                disabled={page === totalPages}
                                className={`flex items-center px-3 py-1 border rounded ml-2 text-base font-medium transition-colors duration-200 ${page === totalPages ? 'bg-gray-100 text-gray-400 border-gray-200 cursor-not-allowed' : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'}`}
                                style={{ minWidth: 80 }}
                            >
                                Next <span className="ml-1">&#8594;</span>
                            </button>
                        </div>
                    )}
                </>
            )}
            <SuccessModal
                isOpen={showSuccessModal}
                onClose={handleCloseSuccessModal}
                orderId={successModalData.orderId}
                action={successModalData.action}
                status={successModalData.status}
            />
        </div>
    );
};

export default ManagementPendingOrders; 
